import { whenPageLoaded } from './afterLoad';

/**
 * Decide *when* to ask the service worker to check for a new deploy.
 *
 * The check itself is already off the main thread — `registration.update()`
 * only kicks the SW, and the SW re-fetches its script and any changed
 * precache entries in its own worker. What was wrong before was the timing:
 * a bare `setInterval(..., 1h)` fires on wall-clock time, so it could land
 * while the user was mid-form or mid-scroll, and it kept firing in background
 * tabs that were never going to be looked at again.
 *
 * So the check is gated on the page being (a) loaded, (b) visible, (c) online
 * and (d) quiet — no pointer, key or scroll activity for IDLE_MS — and is then
 * handed to requestIdleCallback so it lands in a gap rather than competing
 * with whatever the page is doing. A tab coming back to the foreground is also
 * a natural moment to look, since it is usually a fresh glance at the page.
 *
 * MIN_INTERVAL_MS stops all of that from turning into a request per idle gap.
 *
 * Nothing here forces a reload. Finding an update installs it and, via
 * workbox's skipWaiting/clientsClaim, activates it — so the *next* navigation
 * serves the new build while the open page carries on with the one it started
 * with. The page is never swapped underneath the user.
 */
const IDLE_MS = 15_000;
const MIN_INTERVAL_MS = 30 * 60 * 1000;

interface Schedulable {
  update: () => unknown;
}

type Win = Pick<
  Window,
  'addEventListener' | 'removeEventListener' | 'setTimeout' | 'clearTimeout'
> & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
};

export interface ScheduleOptions {
  idleMs?: number;
  minIntervalMs?: number;
  /** Injectable for tests; defaults to the real globals. */
  win?: Win;
  isVisible?: () => boolean;
  isOnline?: () => boolean;
  now?: () => number;
}

/**
 * Start watching for quiet moments and check for a new SW at each one.
 * Returns a cleanup function that removes every listener and timer.
 */
export function scheduleUpdateChecks(
  registration: Schedulable,
  options: ScheduleOptions = {},
): () => void {
  const win = options.win ?? (typeof window !== 'undefined' ? (window as Win) : undefined);
  if (!win) return () => {};

  const idleMs = options.idleMs ?? IDLE_MS;
  const minIntervalMs = options.minIntervalMs ?? MIN_INTERVAL_MS;
  const now = options.now ?? (() => Date.now());
  const isVisible =
    options.isVisible ?? (() => typeof document === 'undefined' || !document.hidden);
  const isOnline = options.isOnline ?? (() => !('onLine' in navigator) || navigator.onLine);

  // Start the clock as if a check had just run, so a fresh load — which has
  // just fetched everything anyway — does not immediately check again.
  let lastCheck = now();
  let idleTimer: number | undefined;
  let stopped = false;

  const check = () => {
    if (stopped || !isVisible() || !isOnline()) return;
    if (now() - lastCheck < minIntervalMs) return;
    lastCheck = now();
    // Hand off to an idle gap. The timeout is a floor, not a promise: if the
    // page never goes idle we still want the check eventually.
    const run = () => {
      try {
        registration.update();
      } catch {
        // A failed update check is not worth surfacing — the next quiet
        // moment tries again, and the user keeps the working cached build.
      }
    };
    if (typeof win.requestIdleCallback === 'function') win.requestIdleCallback(run, { timeout: 10_000 });
    else win.setTimeout(run, 0);
  };

  const armIdleTimer = () => {
    if (stopped) return;
    if (idleTimer !== undefined) win.clearTimeout(idleTimer);
    idleTimer = win.setTimeout(check, idleMs);
  };

  const ACTIVITY = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const;
  for (const type of ACTIVITY) {
    win.addEventListener(type, armIdleTimer, { passive: true, capture: true });
  }
  // Returning to the tab is its own good moment to look for a new deploy.
  win.addEventListener('focus', check);
  win.addEventListener('visibilitychange', check);

  armIdleTimer();

  return () => {
    stopped = true;
    if (idleTimer !== undefined) win.clearTimeout(idleTimer);
    for (const type of ACTIVITY) {
      win.removeEventListener(type, armIdleTimer, { capture: true } as EventListenerOptions);
    }
    win.removeEventListener('focus', check);
    win.removeEventListener('visibilitychange', check);
  };
}

/** Wire the scheduler up once the page has finished loading. */
export function scheduleUpdateChecksAfterLoad(registration: Schedulable): void {
  whenPageLoaded(() => scheduleUpdateChecks(registration));
}
